import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { getUser } from "@faire/retailer-visitor-shared/serialized-data/getUser";
import notifyWhenBackInStock from "@faire/web-api--source/endpoints/www/api/product/itemToken/notify/retailerToken/back-in-stock/post";
import deleteBackInStockNotification from "@faire/web-api--source/endpoints/www/api/product/productOptionToken/notify/retailerToken/back-in-stock/delete";
import { useQuery as useEventSubscriptionsQuery } from "@faire/web-api--source/endpoints/www/api/retailer/retailerToken/event-subscriptions/get-hooks";
import { EventSubscriptionType } from "@faire/web-api--source/indigofair/data/EventSubscriptionType";
import { IEventSubscriptionsCompletionStatusEnum } from "@faire/web-api--source/indigofair/data/IEventSubscriptionsCompletionStatusEnum";
import { UserRole } from "@faire/web-api--source/indigofair/data/UserRole";
import { useCallback, useMemo } from "react";

export const useEventSubscriptionStore = (_: unknown[]) => {
  const { retailerToken } = useRetailer();
  const user = getUser();

  const isValidRetailer = useMemo(
    () =>
      !!(
        retailerToken &&
        user &&
        user.roles &&
        user.roles.indexOf(UserRole.RETAILER) !== -1
      ),
    [retailerToken, user]
  );

  const { data, refetch } = useEventSubscriptionsQuery(
    retailerToken ?? "",
    {
      type: EventSubscriptionType.BACK_IN_STOCK,
      completed:
        IEventSubscriptionsCompletionStatusEnum
          .EventSubscriptionsCompletionStatus.ONLY_INCOMPLETED,
    },
    { enabled: isValidRetailer, refetchOnMount: false }
  );

  const eventSubscriptions = useMemo(
    () => data?.event_subscriptions ?? [],
    [data?.event_subscriptions]
  );

  const subscribeBackInStock = useCallback(
    async (identifier: string) => {
      await notifyWhenBackInStock(identifier, retailerToken!);
      return await refetch();
    },
    [refetch, retailerToken]
  );

  const unsubscribeBackInStock = useCallback(
    async (identifier: string) => {
      await deleteBackInStockNotification(identifier, retailerToken!);
      return await refetch();
    },
    [refetch, retailerToken]
  );

  const BackInStockIdentifiers = useMemo(() => {
    // Return a list of identifiers (which can be product token or product
    // option token) that's back ordered and been subscribed.
    return eventSubscriptions.reduce(
      (accumulator: { [identifer: string]: boolean }, event) => {
        if (event.event_identifier) {
          accumulator[event.event_identifier] = true;
        }
        return accumulator;
      },
      {}
    );
  }, [eventSubscriptions]);

  return {
    eventSubscriptions,
    subscribeBackInStock,
    unsubscribeBackInStock,
    BackInStockIdentifiers,
  };
};
